import { SearchOutlined } from '@ant-design/icons';
import { Alert, Badge, Input, Table, Tag, Tooltip } from 'antd';
import type { ColumnsType, TablePaginationConfig } from 'antd/es/table';
import type { SortOrder, SorterResult } from 'antd/es/table/interface';
import { useEffect, useRef, useState } from 'react';
import { getCreators } from '../api';
import type { CreatorKind, CreatorRow, CreatorSort, CreatorsResponse } from '../contract';
import {
  KIND_LABEL,
  PACE_META,
  creatorName,
  errorText,
  fmtHours,
  fmtKc,
  fmtNum,
  trendTag,
} from '../format';
import { rowOpenProps } from './row-open';
import styles from '../style.module.scss';

interface Props {
  month: string;
  groupId: number | null;
  kind: CreatorKind | null;
  /** Đổi giá trị (vd. sau "Tính lại") → tải lại trang hiện tại. */
  reloadKey: number;
  onOpen: (uid: string) => void;
  /** Tháng không có chuỗi ngày (file tháng TikTok) → ẩn cột "Xu hướng 7 ngày". */
  noSeries?: boolean;
  /** Tháng đã kết thúc (06 E6a) → ẩn "Tiến độ" / "Thiếu ngày" (không còn ý nghĩa). */
  ended?: boolean;
}

const HIDE_WHEN_NO_SERIES = ['delta7'];
const HIDE_WHEN_ENDED = ['pace', 'needDays'];

type Dir = 'asc' | 'desc';
const DEFAULT_SORT: CreatorSort = 'diamonds';
const DEFAULT_DIR: Dir = 'desc';
const SEARCH_DEBOUNCE_MS = 400;

const toOrder = (key: CreatorSort, sort: CreatorSort, dir: Dir): SortOrder =>
  key === sort ? (dir === 'asc' ? 'ascend' : 'descend') : null;

/** Bảng Creator (server-side sort / trang từ GET /analytics/creators). Bấm dòng → Drawer chi tiết. */
const CreatorTable = ({ month, groupId, kind, reloadKey, onOpen, noSeries, ended }: Props) => {
  const [sort, setSort] = useState<CreatorSort>(DEFAULT_SORT);
  const [dir, setDir] = useState<Dir>(DEFAULT_DIR);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(20);
  const [search, setSearch] = useState('');
  const [q, setQ] = useState('');
  const [data, setData] = useState<CreatorsResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const seq = useRef(0);

  // Ô tìm: chờ 400 ms sau phím cuối mới hỏi máy chủ.
  useEffect(() => {
    const t = window.setTimeout(() => setQ(search.trim()), SEARCH_DEBOUNCE_MS);
    return () => window.clearTimeout(t);
  }, [search]);

  // Đổi tháng / nhóm / loại / ô tìm → về trang 1 (không bắn thêm một request với trang cũ).
  const lastFilters = useRef(`${month}|${groupId}|${kind}|${q}`);

  useEffect(() => {
    const sig = `${month}|${groupId}|${kind}|${q}`;
    if (sig !== lastFilters.current) {
      lastFilters.current = sig;
      if (page !== 1) {
        setPage(1);
        return;
      }
    }
    const id = ++seq.current;
    setLoading(true);
    setError(null);
    getCreators({ month, groupId, kind, sort, dir, q: q || null, page, limit })
      .then((res) => {
        if (id === seq.current) setData(res);
      })
      .catch((err) => {
        if (id === seq.current) setError(errorText(err, 'Không tải được danh sách Creator.'));
      })
      .finally(() => {
        if (id === seq.current) setLoading(false);
      });
  }, [month, groupId, kind, sort, dir, q, page, limit, reloadKey]);

  const sortable = (key: CreatorSort) => ({
    key,
    sorter: true,
    sortOrder: toOrder(key, sort, dir),
    sortDirections: ['descend', 'ascend'] as SortOrder[],
  });

  const columns: ColumnsType<CreatorRow> = [
    {
      title: 'Creator',
      key: 'creator',
      fixed: 'left',
      width: 190,
      render: (_, r) => (
        <div className={styles.cellCreator}>
          <span className={styles.cellName}>{creatorName(r)}</span>
          {r.groupName && <span className={styles.cellGroup}>{r.groupName}</span>}
        </div>
      ),
    },
    {
      title: 'Loại',
      key: 'kind',
      width: 100,
      render: (_, r) => <Tag>{KIND_LABEL[r.kind] || r.kind}</Tag>,
    },
    {
      title: 'KC',
      dataIndex: 'diamonds',
      ...sortable('diamonds'),
      width: 120,
      align: 'right',
      render: (v: number) => <strong>{fmtKc(v)}</strong>,
    },
    {
      title: 'Giờ LIVE',
      dataIndex: 'liveHours',
      ...sortable('liveSeconds'),
      width: 120,
      align: 'right',
      render: (v: number) => fmtHours(v),
    },
    {
      title: 'Ngày hợp lệ',
      dataIndex: 'liveDays',
      ...sortable('liveDays'),
      width: 120,
      align: 'right',
      render: (v: number) => fmtNum(v),
    },
    {
      title: 'Xu hướng 7 ngày',
      dataIndex: 'delta7Pct',
      ...sortable('delta7'),
      width: 150,
      render: (_, r) => {
        const t = trendTag(r.trend, r.delta7Pct);
        return (
          <span className={styles.trend} style={{ color: t.color }} title={t.word}>
            {t.text}
          </span>
        );
      },
    },
    {
      title: 'Tiến độ',
      key: 'pace',
      width: 130,
      render: (_, r) => {
        const meta = r.paceStatus ? PACE_META[r.paceStatus] : null;
        return meta ? (
          <Tag color={meta.color}>{meta.label}</Tag>
        ) : (
          <span className={styles.muted}>—</span>
        );
      },
    },
    {
      title: 'Thiếu ngày',
      dataIndex: 'needDays',
      ...sortable('needDays'),
      width: 120,
      align: 'right',
      render: (v: number | null) => (v === null || v === undefined ? '—' : fmtNum(v)),
    },
    {
      title: 'Chú ý',
      dataIndex: 'dangerCount',
      ...sortable('attention'),
      width: 130,
      render: (_, r) => {
        if (!r.dangerCount && !r.warnCount) return <span className={styles.muted}>—</span>;
        const badges = (
          <span className={styles.attention}>
            {r.dangerCount > 0 && <Badge color="#dc2626" text={`${r.dangerCount} nặng`} />}
            {r.warnCount > 0 && <Badge color="#E8B04B" text={`${r.warnCount} nhẹ`} />}
          </span>
        );
        return r.topInsight ? <Tooltip title={r.topInsight.title}>{badges}</Tooltip> : badges;
      },
    },
  ];

  const onChange = (
    pagination: TablePaginationConfig,
    _filters: unknown,
    sorter: SorterResult<CreatorRow> | SorterResult<CreatorRow>[]
  ) => {
    const s = Array.isArray(sorter) ? sorter[0] : sorter;
    const nextSort = (s?.order ? (s.columnKey as CreatorSort) : DEFAULT_SORT) || DEFAULT_SORT;
    const nextDir: Dir = s?.order ? (s.order === 'ascend' ? 'asc' : 'desc') : DEFAULT_DIR;
    const sortChanged = nextSort !== sort || nextDir !== dir;
    setSort(nextSort);
    setDir(nextDir);
    const nextLimit = pagination.pageSize || limit;
    setLimit(nextLimit);
    setPage(sortChanged || nextLimit !== limit ? 1 : pagination.current || 1);
  };

  return (
    <section className={styles.card} aria-label="Bảng Creator">
      <div className={styles.cardHead}>
        <h2 className={styles.cardTitle}>
          Creator{data ? <span className={styles.count}> · {fmtNum(data.total)}</span> : null}
        </h2>
        <Input
          allowClear
          className={styles.search}
          prefix={<SearchOutlined />}
          placeholder="Tìm @TikTok ID hoặc UID"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          maxLength={100}
          aria-label="Tìm Creator theo TikTok ID hoặc UID"
        />
      </div>
      {error && <Alert type="error" showIcon message={error} className={styles.section} />}
      <Table<CreatorRow>
        size="middle"
        rowKey="uid"
        loading={loading}
        columns={columns.filter((c) => {
          const key = String(c.key ?? '');
          if (noSeries && HIDE_WHEN_NO_SERIES.includes(key)) return false;
          return !(ended && HIDE_WHEN_ENDED.includes(key));
        })}
        dataSource={data?.rows || []}
        onChange={onChange}
        scroll={{ x: 1180 }}
        rowClassName={() => styles.clickRow}
        onRow={(r) => rowOpenProps(`Xem chi tiết ${creatorName(r)}`, () => onOpen(r.uid))}
        pagination={{
          current: page,
          pageSize: limit,
          total: data?.total || 0,
          showSizeChanger: true,
          pageSizeOptions: [20, 50, 100],
          showTotal: (total) => `${fmtNum(total)} Creator`,
        }}
        locale={{ emptyText: q ? 'Không có Creator khớp ô tìm.' : 'Chưa có Creator nào.' }}
      />
    </section>
  );
};

export default CreatorTable;
